import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { Button } from '../ds/primitives/Button'
import { useT } from '../lib/i18n'
import { usePublicPath } from '../lib/publicPaths'

const STORAGE_KEY = 'edimi_cookie_ok'
const CONSENT_KEY = 'edimi_cookie_consent'

/* The version of the BANNER COPY this record was given against. Bump it when the
 * wording changes, exactly as the publishing consent carries the version of the
 * sentence it was given against (SPEC A7). A record without a version cannot say
 * WHAT somebody agreed to, only that they clicked.
 *
 * 2026-09-07 (paczka 07): the banner became ONE sentence and ONE button. The copy
 * now agrees with §4 of the privacy policy — no consent is asked, because nothing
 * stored needs one — so the version moves. */
const CONSENT_COPY_VERSION = '2026-09-07'

/* What this product actually stores on the device today, measured rather than
 * assumed: one Flask session cookie, strictly necessary (SESSION_COOKIE_SECURE /
 * HTTPONLY / SAMESITE=Lax), and NOTHING else — no gtag, no plausible, no posthog,
 * no matomo, client side or server side.
 *
 * So the recorded SCOPE is `necessary`. That is not a shortcut, it is the whole
 * point: a click on this bar today cannot authorise analytics introduced in three
 * months, because consent has to be specific and informed about processing that
 * actually happens. A future analytics module must require a record whose scope
 * CONTAINS `analytics` — and no record written today ever will, so it will have to
 * ask (and the two-button choice comes back with it, MAPA 07 decision 5). */
type ConsentScope = 'necessary' | 'analytics'

interface ConsentRecord {
  v: 1
  /** What the person pressed — kept because it is what they did. */
  choice: 'all' | 'necessary'
  /** What they actually consented to, which is what a reader must check. */
  scope: ConsentScope[]
  copyVersion: string
  at: string
}

/**
 * The banner RESERVES space; it does not borrow it.
 *
 * Twice today it was found painted on top of something that mattered: the
 * composer's consent gate (P02) and PackQa's failure toast — the only channel
 * that says a bulk DELETE did not go through (P04). Both were invisible on a
 * first visit, which is the only visit where the banner exists at all.
 *
 * The tempting fix is a z-index auction, and P04 was right to refuse to enter
 * one: winning it means the toast covers the cookie banner instead, and the
 * banner is itself a legal gate. The fix is for the banner to publish its own
 * height, so anything anchored to the bottom edge can sit above it:
 *
 *     --cookie-bar-h   its height in px while it is on screen, else 0px
 *
 *     .my-bottom-bar { bottom: calc(24px + var(--cookie-bar-h, 0px)); }
 *
 * The fallback matters as much as the value: `var(--cookie-bar-h, 0px)` is
 * correct on every page where this component never mounts, which is most of
 * them after the first visit. A consumer that reads it needs no knowledge of
 * whether the banner is showing.
 *
 * Below 560px the banner also moves to the TOP (public.css), so the variable is
 * belt and braces there — it is the wider screens, where the banner stays at the
 * bottom, that still need it.
 */
export interface CookieBarProps {
  /**
   * Czy pasek stoi na ekranie.
   *
   * Paczka 37b: strona główna ma na 390 własny pasek akcji przy dolnej krawędzi i NIE
   * wolno mu stanąć razem z tym paskiem (pułapka rundy R2 — dwa paski przy tej samej
   * krawędzi zasłaniają się nawzajem, a ten jest gatem prawnym). Pytanie „czy pasek
   * cookie jest widoczny” ma tylko jedną prawdziwą odpowiedź i mieszka ona tutaj:
   * czytanie `localStorage` z zewnątrz rozjeżdża się w chwili, w której ktoś zamknie
   * pasek przy otwartej stronie.
   */
  onVisible?: (visible: boolean) => void
}

export default function CookieBar({ onVisible }: CookieBarProps = {}) {
  const t = useT()
  const pub = usePublicPath()
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== '1'
    } catch {
      return false
    }
  })

  /** Publish the height, and clear it the moment the banner goes. */
  const publishHeight = useCallback(() => {
    const el = ref.current
    const root = document.documentElement
    if (!el) {
      root.style.removeProperty('--cookie-bar-h')
      return
    }
    // Its own bottom offset counts: an element that clears the banner has to
    // clear the gap under it too, or it lands in the shadow.
    //
    // ONLY WHILE IT SITS ON THE BOTTOM EDGE. Below 560px public.css moves the bar to the
    // top (`inset: 0 0 auto 0`), and for a fixed element with `bottom: auto` the computed
    // `bottom` is the USED distance from the bottom edge - measured on ed6e60aa at 390px:
    // 687.9px, so the variable read 844px (the whole viewport) and every consumer that
    // offsets by it would have left the screen. A bar at the top reserves nothing at the
    // bottom; it publishes 0px, which is what the fallback promises anyway.
    //
    // Paczka 37 odsunęła pasek od krawędzi (24 px na 1440, 12 px na 390 — pasek stoi
    // na siatce strony, nie na krawędzi ekranu), więc „dotyka dołu” przestało być
    // testem na równość. Teraz: dolna krawędź nie wychodzi poza ekran, a górna leży
    // w dolnej połowie — pasek U GÓRY dalej rezerwuje 0 px, co obiecuje fallback.
    const rect = el.getBoundingClientRect()
    const onBottomEdge = rect.bottom <= window.innerHeight + 1 && rect.top > window.innerHeight / 2
    const gap = onBottomEdge ? (parseFloat(getComputedStyle(el).bottom) || 0) : 0
    const reserve = onBottomEdge ? Math.round(el.offsetHeight + gap) : 0
    root.style.setProperty('--cookie-bar-h', `${reserve}px`)
  }, [])

  // useLayoutEffect, not useEffect: the value has to be there before the browser
  // paints, or every bottom-anchored element renders once in the wrong place.
  useLayoutEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty('--cookie-bar-h')
      return
    }
    publishHeight()
    // Braces on purpose: `removeProperty` returns a string, and an effect cleanup
    // that returns anything is a type error.
    return () => {
      document.documentElement.style.removeProperty('--cookie-bar-h')
    }
  }, [visible, publishHeight])

  // The banner's height is not fixed: the copy wraps differently per language and
  // per width, and it is taller on a phone than on a laptop.
  useEffect(() => {
    if (!visible || typeof ResizeObserver === 'undefined') return
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(publishHeight)
    ro.observe(el)
    window.addEventListener('resize', publishHeight)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', publishHeight)
    }
  }, [visible, publishHeight])

  // Osobny efekt, nie wywołanie w trakcie renderu: zawiadomienie rodzica jest zmianą
  // JEGO stanu, a ta nie może się wydarzyć w środku renderu dziecka.
  useEffect(() => { onVisible?.(visible) }, [visible, onVisible])
  useEffect(() => () => onVisible?.(false), [onVisible])

  if (!visible) return null

  /* One button, one record: the person acknowledged the information. The record
   * carries SCOPE and VERSION, not a boolean — the same shape the publishing
   * consent already uses — and the scope is `necessary` whatever the future
   * banner says, because that is all this product stores. */
  const dismiss = () => {
    const record: ConsentRecord = {
      v: 1,
      choice: 'necessary',
      scope: ['necessary'],
      copyVersion: CONSENT_COPY_VERSION,
      at: new Date().toISOString(),
    }
    try {
      localStorage.setItem(STORAGE_KEY, '1')
      localStorage.setItem(CONSENT_KEY, JSON.stringify(record))
    } catch { /* private mode */ }
    // Clear before the state change so nothing paints a frame with the old
    // reservation and a banner that is already gone.
    document.documentElement.style.removeProperty('--cookie-bar-h')
    setVisible(false)
  }

  /* Paczka 37 (artboard 01, R2 rozstrzygnięcie 1): JEDNA LINIA na siatce strony —
     x = 100 do 1340 przy 1440, wysokość 64 px, 24 px od dolnej krawędzi; na 390 pas
     92 px przy dolnej krawędzi pierwszego ekranu. Tytuł paska zniknął (zasada 8):
     zdanie mówi wszystko, a link jest nazwą dokumentu, nie ogonem zdania. */
  return (
    <div className="cookie-bar mp-cookie" role="region" aria-label={t('cookie.aria')} ref={ref}>
      <p>
        {t('cookie.bodyMinimal')}{' '}
        {/* §4 of the policy is the section that says no consent is asked. */}
        <Link to={pub('/polityka#p4')}>{t('cookie.policyMinimal')}</Link>
      </p>
      <Button variant="secondary" onClick={dismiss}>{t('cookie.dismiss')}</Button>
    </div>
  )
}
